import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MapPin, Phone, Mail, ShoppingBag, FileText, ExternalLink, Zap } from 'lucide-react';
import { useTheme } from './ThemeProvider';

import { API } from '@/utils/apiConfig';

const TopBar = () => {
  const { theme } = useTheme();
  const [settings, setSettings] = useState({
    address: '', phone: '', email: '',
    eshop_url: '', eshop_label: 'ESHOP',
    blog_url: '', blog_label: 'BLOG',
    custom_link_url: '', custom_link_label: '',
    custom_link_mobile: false,
    nav_extra_url: '', nav_extra_label: '',
    facebook_url: '', instagram_url: '',
    right_links_mode: 'icon_text'
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/topbar`);
        setSettings(prev => ({ ...prev, ...response.data }));
      } catch (error) {
        console.error('Error fetching topbar settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const hasLinks = settings.eshop_url || settings.blog_url || settings.custom_link_url;
  const iconOnly = settings.right_links_mode === 'icon';

  return (
    <div
      className="text-[10px] sm:text-xs lg:text-sm"
      style={{ background: `linear-gradient(135deg, ${theme.topbar_bg} 0%, ${theme.topbar_bg}dd 100%)`, color: theme.topbar_text }}
      data-testid="top-bar"
    >
      <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16">
        <div className="flex items-center justify-between h-8 sm:h-9 lg:h-10">

          {/* Left: Address + Phone */}
          <div className="flex items-center gap-0.5 sm:gap-1 lg:gap-2 overflow-hidden min-w-0">
            {settings.address && (
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(settings.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full transition-all duration-300 hover:bg-white/15 shrink-0"
                style={{ color: theme.topbar_text }}
                data-testid="topbar-address"
              >
                <MapPin className="w-3 h-3 flex-shrink-0 opacity-70" />
                <span className="hidden lg:inline truncate max-w-[200px]">{settings.address}</span>
              </a>
            )}

            <span className="hidden lg:block w-px h-4 bg-white/20 mx-0.5" />

            {settings.phone && (
              <a
                href={`tel:${settings.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full transition-all duration-300 hover:bg-white/15 shrink-0"
                style={{ color: theme.topbar_text }}
                data-testid="topbar-phone"
              >
                <Phone className="w-3 h-3 flex-shrink-0 opacity-70" />
                <span className="hidden sm:inline whitespace-nowrap">{settings.phone}</span>
              </a>
            )}

            {settings.email && (
              <a
                href={`mailto:${settings.email}`}
                className="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full transition-all duration-300 hover:bg-white/15 shrink-0"
                style={{ color: theme.topbar_text }}
                data-testid="topbar-email"
              >
                <Mail className="w-3 h-3 flex-shrink-0 opacity-70" />
                <span className="hidden xl:inline whitespace-nowrap">{settings.email}</span>
              </a>
            )}
          </div>

          {/* Right: Eshop/Blog links */}
          <div className="flex items-center gap-0.5 sm:gap-1 lg:gap-2 shrink-0">
            {hasLinks && (
              <>
                {settings.eshop_url && (
                  <a
                    href={settings.eshop_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full font-semibold tracking-wide transition-all duration-300 hover:bg-white/15"
                    style={{ color: theme.topbar_text }}
                    title={settings.eshop_label}
                    data-testid="topbar-eshop"
                  >
                    <ShoppingBag className="w-3 h-3 flex-shrink-0 opacity-70" />
                    {!iconOnly && <span className="hidden sm:inline">{settings.eshop_label}</span>}
                    {!iconOnly && <ExternalLink className="w-2.5 h-2.5 opacity-50 hidden sm:block" />}
                  </a>
                )}

                {settings.blog_url && (
                  <a
                    href={settings.blog_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full font-semibold tracking-wide transition-all duration-300 hover:bg-white/15"
                    style={{ color: theme.topbar_text }}
                    title={settings.blog_label}
                    data-testid="topbar-blog"
                  >
                    <FileText className="w-3 h-3 flex-shrink-0 opacity-70" />
                    {!iconOnly && <span className="hidden sm:inline">{settings.blog_label}</span>}
                    {!iconOnly && <ExternalLink className="w-2.5 h-2.5 opacity-50 hidden sm:block" />}
                  </a>
                )}

                {settings.custom_link_url && (
                <a
                  href={settings.custom_link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${settings.custom_link_mobile ? 'flex' : 'hidden sm:flex'} items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full font-semibold tracking-wide transition-all duration-300 hover:bg-white/15`}
                  style={{ color: theme.topbar_text }}
                  title={settings.custom_link_label || 'ODKAZ'}
                  data-testid="topbar-custom-link"
                >
                  <Zap className="w-3 h-3 flex-shrink-0 opacity-70" />
                  {!iconOnly && <span className="hidden sm:inline">{settings.custom_link_label || 'ODKAZ'}</span>}
                  {!iconOnly && <ExternalLink className="w-2.5 h-2.5 opacity-50 hidden sm:block" />}
                </a>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopBar;
